'use client';

import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import type { Map } from '@alltrails/types/Map';
import PhotoSizeDimensions from '@alltrails/types/PhotoSizeDimensions';
import StaticMapSize from '@alltrails/types/StaticMapSize';
import { getCustomRouteUrl } from '../../utils/cardLinks';
import useMapPhotos from '../../hooks/useMapPhotos';
import type { CardProps, InternalCardProps, MapCardVariant } from '../../types';
import MapMetadata from '../MapMetadata';
import SmallCard from '../SmallCard';
import StandardCard from '../StandardCard';

// Cache photo load states to prevent re-checking on component remount
// In the case of a custom route, if the photo fails, we don't want to keep trying to load it on every remount
const photoLoadCache = new Map<string, 'loaded' | 'failed'>();

const getPhotoSize = (variant: MapCardVariant): PhotoSizeDimensions => {
  switch (variant) {
    case 'small':
      return '160x160'; // LargeSquare
    case 'standard':
    default:
      return '1080x700'; // LargerWide
  }
};

const getMapSize = (variant: MapCardVariant): StaticMapSize => {
  switch (variant) {
    case 'small':
      return '160x160';
    case 'standard':
    default:
      return '320x320';
  }
};

const getMaxPhotos = (variant: MapCardVariant, maxPhotos: number, isCustomRoute?: boolean) => {
  switch (variant) {
    case 'small':
      return 1;
    case 'standard':
    default:
      return isCustomRoute ? 1 : maxPhotos;
  }
};

export type MapCardProps = CardProps & {
  variant: MapCardVariant;
  map: Map;
  /**
   * Whether to display the attribution text.
   * If true, the attribution text will be displayed as an overlay on the card.
   * If false, the attribution text will not be displayed as an overlay. The owner name will be displayed in the metadata.
   * @default false
   */
  displayAttribution?: boolean;
  isFirstParty?: boolean;
  blockNavigation?: boolean; // Prevent the map links from actually navigating - the monolith handles all navigation from the onClick handlers
};

const MapCard = ({
  className,
  variant,
  map,
  displayAttribution = false,
  isFirstParty = false,
  languageRegionCode,
  onCardClick,
  onCardMouseEnter,
  onCardMouseLeave,
  onFavoriteClick,
  isFavorite,
  openInNewTab,
  useMetric,
  decimalPlaces = 1,
  maxPhotos = 6,
  baseUrl = '',
  dragHandleProps,
  deleteButton,
  adminCTAs,
  blockNavigation,
  isCustomRoute
}: MapCardProps) => {
  const intl = useIntl();
  const { name, slug, id, hasPhotos } = map;
  const href = getCustomRouteUrl(slug, baseUrl, languageRegionCode);
  const { staticMap, photos, loadPhotos } = useMapPhotos(
    id,
    getPhotoSize(variant),
    getMapSize(variant),
    getMaxPhotos(variant, maxPhotos, isCustomRoute),
    baseUrl,
    isCustomRoute
  );

  const onMouseEnter = () => {
    loadPhotos();
    onCardMouseEnter?.();
  };

  const metadata = (
    <MapMetadata
      map={map}
      displayAttribution={displayAttribution}
      isFirstParty={isFirstParty}
      useMetric={useMetric}
      decimalPlaces={decimalPlaces}
      baseUrl={baseUrl}
      openInNewTab={openInNewTab}
    />
  );

  const cardProps: InternalCardProps = {
    className,
    name,
    href,
    openInNewTab,
    metadata,
    onCardClick,
    onCardMouseEnter: onMouseEnter,
    onCardMouseLeave,
    onFavoriteClick,
    isFavorite,
    blockNavigation
  };

  const [customRoutePhotoState, setCustomRoutePhotoState] = useState<'loading' | 'loaded' | 'failed'>('loading');

  useEffect(() => {
    if (!isCustomRoute || !photos[0]) {
      setCustomRoutePhotoState('failed');
      return;
    }

    // Check cache first to avoid re-checking on remount
    const cachedState = photoLoadCache.get(photos[0]);
    if (cachedState) {
      setCustomRoutePhotoState(cachedState);
      return;
    }

    setCustomRoutePhotoState('loading');

    // Preload the image to check if it exists
    const img = new Image();

    img.onload = () => {
      photoLoadCache.set(photos[0], 'loaded');
      setCustomRoutePhotoState('loaded');
    };

    img.onerror = () => {
      photoLoadCache.set(photos[0], 'failed');
      setCustomRoutePhotoState('failed');
    };

    img.src = photos[0];

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [isCustomRoute, photos]);

  const standardCardPhotos = useMemo(() => {
    if (isCustomRoute) {
      if (customRoutePhotoState === 'loading') {
        return [];
      }
      if (customRoutePhotoState === 'loaded' && photos[0]) {
        return [photos[0]];
      }
      return [staticMap];
    }
    if (hasPhotos) {
      return [staticMap, ...photos];
    }

    return [staticMap];
  }, [isCustomRoute, customRoutePhotoState, hasPhotos, photos, staticMap]);

  const smallCardPhoto = useMemo(() => {
    if (isCustomRoute) {
      if (customRoutePhotoState === 'loaded' && photos[0]) {
        return photos[0];
      }
      return staticMap;
    }
    return staticMap;
  }, [isCustomRoute, customRoutePhotoState, photos, staticMap]);

  const attributionText = useMemo(() => {
    if (displayAttribution) {
      if (isFirstParty) {
        return intl.formatMessage({ defaultMessage: 'Created by you' });
      }

      const {
        user: { name: userFullName, firstName, lastName }
      } = map;
      const displayName = firstName && lastName ? `${firstName} ${lastName[0]}.` : userFullName;
      return intl.formatMessage({ defaultMessage: 'Created by {name}' }, { name: displayName });
    }
    return undefined;
  }, [displayAttribution, isFirstParty, map, intl]);

  switch (variant) {
    case 'small':
      return <SmallCard {...cardProps} photo={smallCardPhoto} adminCTAs={adminCTAs} />;
    case 'standard':
    default:
      return (
        <StandardCard
          {...cardProps}
          attributionText={attributionText}
          photos={standardCardPhotos}
          loadPhotos={loadPhotos}
          dragHandleProps={dragHandleProps}
          deleteButton={deleteButton}
          isLoadingPhotos={isCustomRoute && customRoutePhotoState === 'loading'}
        />
      );
  }
};

export default MapCard;
